import { lazy, Suspense, useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { MainLayout } from "@/layouts/main-layout";
import { ChatLayout } from "@/layouts/chat-layout";
import { AuthProvider } from "@/contexts/auth-context";
import { ScrollToTop } from "@/components/common";
import { NavigatorLayout } from "@/layouts/navigator-layout";
import { RequireAccess } from "@/components/auth/require-access";
import { nvLoginPath } from "@/components/navigator/nv-paths";
import { NavigatorLoginPage } from "@/pages/navigator/auth/login-page";
import { NavigatorSignupPage } from "@/pages/navigator/auth/signup-page";
import {
  NavigatorInviteCompletePage,
  NavigatorSignupCompletePage,
} from "@/pages/navigator/auth/signup-complete-page";
import { NavigatorTermsPage } from "@/pages/navigator/legal/terms-page";
import ProjectListPage from "@/pages/projects/project-list-page";
import { ChatPage } from "@/pages/chat/chat-page";
import { AI_ASSISTANT_ENABLED } from "@/lib/feature-flags";
import { NavigatorAiAssistantNoticePage } from "@/pages/navigator/ai-assistant/ai-assistant-notice-page";
import ProjectReportPage from "@/pages/project-report/project-report-page";
import ShortlistPage from "@/pages/shortlist/shortlist-page";
import AgentComparePage from "@/pages/compare-agent/agent-compare-page";
import { useAuth } from "@/contexts/auth-context";
import { NavigatorContactPage } from "@/pages/navigator/contact/contact-page";
import { NavigatorContactCompletePage } from "@/pages/navigator/contact/contact-complete-page";
import { NavigatorCaseStudyListPage } from "@/pages/navigator/case-study/case-study-list-page";
import { NavigatorCaseStudyDetailPage } from "@/pages/navigator/case-study/case-study-detail-page";
import { NavigatorAgentFinderPage } from "@/pages/navigator/agent-finder/agent-finder-page";
import { NavigatorHomePage } from "@/pages/navigator/home/home-page";
import { NavigatorMyPageLayout } from "@/pages/navigator/my-page/my-page-layout";
import { MyProfileSection } from "@/pages/navigator/my-page/my-profile";
import {
  MyBookmarksSection,
  MyCompareSection,
  MyInquiriesSection,
  MyInquiryDetailSection,
  MyRecentSection,
} from "@/pages/navigator/my-page/my-sections";
import { NavigatorAgentDetailPage } from "@/pages/navigator/agent-detail/agent-detail-page";
import { NavigatorAgentDefinitionPage } from "@/pages/navigator/agent-detail/agent-definition-page";
// AX News 임시 비공개 (저작권 이슈, 2026-10-02) — 복구 시 아래 import 3줄과 /ax-news 라우트 3줄의 주석을 해제
// import AXNews from "./pages/ax-news/Ax-news";
// import AXListPage from "./pages/ax-news/ax-list-page";
// import AxNewsDetail from "./pages/ax-news/ax-news-detail";
import { NotFoundPage } from "./pages/error/not-found-page";
import { RouterErrorElement } from "./pages/error/router-error-element";
import { ServerErrorRoute } from "./pages/error/server-error-route";
import ProjectReportChecklist from "./pages/project-report/project-report-checklist";

function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return null;

  // Keep the whole address (query included) so login sends the user back to the page they were on
  if (!isAuthenticated) {
    return (
      <Navigate to={nvLoginPath(location.pathname + location.search)} replace />
    );
  }

  return <Outlet />;
}

function GlobalNavigationListener() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleServerError = (event: any) => {
      const status = event.detail?.status || 500;
      navigate(`/server-error?status=${status}`, { replace: true });
    };

    const handleNotFound = () => {
      navigate("/404", { replace: true });
    };

    window.addEventListener("server:error", handleServerError);
    window.addEventListener("server:not-found", handleNotFound);

    return () => {
      window.removeEventListener("server:error", handleServerError);
      window.removeEventListener("server:not-found", handleNotFound);
    };
  }, [navigate]);

  return null;
}

// 옛 문의 상세 주소 → My Page 문의 상세 (가정 A14: 옛 화면 파일은 남겨 둠)
function LegacyInquiryRedirect() {
  const { id = "" } = useParams();
  return <Navigate to={`/my-page/inquiries/${id}`} replace />;
}

function AssistantRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

const ChatPreviewPage =
  import.meta.env.DEV && AI_ASSISTANT_ENABLED
    ? lazy(() =>
        import("./pages/chat/chat-preview-page").then((module) => ({
          default: module.ChatPreviewPage,
        })),
      )
    : null;

const NavigatorUiPreviewPage = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/dev/navigator-ui-preview-page").then((module) => ({
        default: module.NavigatorUiPreviewPage,
      })),
    )
  : null;

const DevLoginPage = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/dev/dev-login-page").then((module) => ({
        default: module.DevLoginPage,
      })),
    )
  : null;

export const router = createBrowserRouter([
  {
    element: (
      <>
        <ScrollToTop />
        <AuthProvider>
          <GlobalNavigationListener />
          <Outlet />
        </AuthProvider>
      </>
    ),
    errorElement: <RouterErrorElement />,
    children: [
      {
        element: <MainLayout />,
        children: [
          // { path: "/ax-news", element: <AXNews /> },
          // { path: "/ax-news/list", element: <AXListPage /> },
          // { path: "/ax-news/list/:id", element: <AxNewsDetail /> },
          {
            element: <ProtectedRoute />,
            children: [
              { path: "/projects", element: <ProjectListPage /> },
              {
                path: "/projects/checklist",
                element: <ProjectReportChecklist />,
              },
              { path: "/projects/:id/report", element: <ProjectReportPage /> },
              { path: "/projects/:id/shortlist", element: <ShortlistPage /> },
              { path: "/compare-agent", element: <AgentComparePage /> },
            ],
          },
        ],
      },
      {
        // AI Agent Navigator screens (GNB 4종 + Footer)
        element: <NavigatorLayout />,
        children: [
          // HOME (F00): 누구나, 영역별 노출은 서버가 등급으로 정해요. 옛 랜딩 화면 파일은 남겨 둠 (가정 A14)
          { path: "/", element: <NavigatorHomePage /> },
          { path: "/login", element: <NavigatorLoginPage /> },
          // Temporary preparation notice, controlled by the frontend build environment.
          {
            path: "/ai-assistant",
            element: AI_ASSISTANT_ENABLED ? (
              <AssistantRedirect to="/chat" />
            ) : (
              <NavigatorAiAssistantNoticePage />
            ),
          },
          // Contact Us: 회원 · 비회원 폼은 화면이 사용자 등급으로 나눠요 (F05)
          { path: "/contact", element: <NavigatorContactPage /> },
          {
            path: "/contact/complete",
            element: <NavigatorContactCompletePage />,
          },
          // Case Study: 목록은 누구나(△ 미리보기), 상세는 서버가 등급을 확인해요 (F02)
          { path: "/case-study", element: <NavigatorCaseStudyListPage /> },
          {
            path: "/case-study/:id",
            element: <NavigatorCaseStudyDetailPage />,
          },
          // Agent Finder · 에이전트 상세 (F01 · F01-1): 목록은 누구나(△ Blur), 상세 · 정의서는 서버가 등급을 확인해요.
          // 옛 /agents 목록은 Agent Finder로 보내요 (옛 화면 파일은 남겨 둠, 가정 A14)
          { path: "/agent-finder", element: <NavigatorAgentFinderPage /> },
          { path: "/agents", element: <Navigate to="/agent-finder" replace /> },
          { path: "/agents/:id", element: <NavigatorAgentDetailPage /> },
          {
            path: "/agents/:id/definition",
            element: <NavigatorAgentDefinitionPage />,
          },
          // My Page (F07 · F09, Figma 09 가로형): 수요자 · PwC 내부(공급자는 내 정보 · 문의 이력만), 옛 문의 화면 주소는 여기로
          {
            path: "/my-page",
            element: <NavigatorMyPageLayout />,
            children: [
              { index: true, element: <MyProfileSection /> },
              { path: "bookmarks", element: <MyBookmarksSection /> },
              { path: "compare", element: <MyCompareSection /> },
              { path: "inquiries", element: <MyInquiriesSection /> },
              { path: "inquiries/:id", element: <MyInquiryDetailSection /> },
              { path: "recent", element: <MyRecentSection /> },
            ],
          },
          // 푸터의 이용약관 · 개인정보처리방침: 누구나. 둘 다 DB의 현재 약관 문서를 보여줘요.
          { path: "/terms", element: <NavigatorTermsPage /> },
          {
            path: "/privacy",
            element: <NavigatorTermsPage code="privacy_policy" />,
          },
          {
            path: "/inquiries",
            element: <Navigate to="/my-page/inquiries" replace />,
          },
          { path: "/inquiries/:id", element: <LegacyInquiryRedirect /> },
          // 사전 초대 가입을 마치면 바로 로그인 상태가 되므로 가입 완료 안내는 접근 제한 밖에 둬요
          {
            path: "/signup/invite-complete",
            element: <NavigatorInviteCompletePage />,
          },
          {
            element: <RequireAccess allow={["guest", "pending"]} />,
            children: [
              { path: "/signup", element: <NavigatorSignupPage /> },
              {
                path: "/signup/complete",
                element: <NavigatorSignupCompletePage />,
              },
            ],
          },
        ],
      },
      {
        element: <ChatLayout />,
        children: [
          ...(ChatPreviewPage
            ? [
                {
                  path: "/chat/preview",
                  element: (
                    <Suspense fallback={null}>
                      <ChatPreviewPage />
                    </Suspense>
                  ),
                },
              ]
            : []),
          {
            path: "/chat",
            element: AI_ASSISTANT_ENABLED ? (
              <ChatPage />
            ) : (
              <AssistantRedirect to="/ai-assistant" />
            ),
          },
        ],
      },

      ...(NavigatorUiPreviewPage
        ? [
            {
              path: "/__dev/navigator-ui",
              element: (
                <Suspense fallback={null}>
                  <NavigatorUiPreviewPage />
                </Suspense>
              ),
            },
          ]
        : []),
      ...(DevLoginPage
        ? [
            {
              path: "/__dev/login-as",
              element: (
                <Suspense fallback={null}>
                  <DevLoginPage />
                </Suspense>
              ),
            },
          ]
        : []),
      { path: "/404", element: <NotFoundPage /> },
      { path: "/server-error", element: <ServerErrorRoute /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
